import { useEffect, useRef, type ReactNode } from "react";
import { useNavigate } from "./navigation.js";
import { type Activity } from "../net/api.js";
import { playerStore } from "../net/playerStore.js";
import { ChannelProvider, usePlayerActivity, usePlayerState } from "./ChannelProvider.js";

/**
 * The player's current activity, and the navigation that follows from it.
 *
 * This used to be a poll: every fifteen seconds, and every 750 milliseconds
 * while a player was queueing, because a table can form between two ticks and
 * the client had no other way to find out. Each of those requests woke the
 * player's Durable Object twice and wrote a row to its storage.
 *
 * It is now a view over the channel store. The matchmaker publishes the two
 * transitions this component existed to catch — a prep room opening and a table
 * being dealt — at the moment they happen, so the answer arrives sooner and
 * costs nothing while nothing is happening.
 */

interface ActivityValue {
  activity: Activity | null;
  loading: boolean;
  refresh: () => Promise<Activity | null>;
}

export function ActivityProvider({ children }: { children: ReactNode }) {
  return (
    <ChannelProvider>
      <ReadyRoomNavigator />
      {children}
    </ChannelProvider>
  );
}

/**
 * Take the player to the table the server just dealt them.
 *
 * The prep room used to poll twice a second waiting for exactly this. Now
 * `room.ready` arrives on the channel and the navigation happens from one place
 * rather than from whichever screen happened to be mounted.
 */
function ReadyRoomNavigator() {
  const navigate = useNavigate();
  const pendingRoomId = usePlayerState((state) => state.pendingRoomId);
  const activity = usePlayerState((state) => state.activity);
  const promptedLobby = useRef<string | null>(null);
  const lobbyCode = activity.type === "queue" && activity.state === "prepping" ? activity.lobbyCode : null;
  useEffect(() => {
    if (!lobbyCode) { promptedLobby.current = null; return; }
    if (pendingRoomId || promptedLobby.current === lobbyCode) return;
    promptedLobby.current = lobbyCode;
    navigate(`/room/${lobbyCode}`);
  }, [lobbyCode, navigate, pendingRoomId]);
  useEffect(() => {
    if (pendingRoomId === null) return;
    // Cleared before navigating, so a re-render cannot bounce the player back.
    playerStore.set((state) => ({ ...state, pendingRoomId: null }));
    navigate(`/table/${pendingRoomId}`);
  }, [pendingRoomId, navigate]);
  return null;
}

export function useActivity(): ActivityValue {
  const { activity, refresh } = usePlayerActivity();
  return { activity, loading: false, refresh };
}
